import { useQuery, type UseQueryOptions, type UseQueryResult, type QueryKey } from '@tanstack/react-query';
import { BACKEND_URL, API_VERSION } from '@/config/api';

let globalLogoutCallback: (() => void) | null = null;

export const setGlobalLogoutCallback = (callback: () => void) => {
  globalLogoutCallback = callback;
};

export type Primitive = string | number | boolean | null | undefined;
export type QueryParamValue = Primitive | Primitive[];
export type QueryParams = Record<string, QueryParamValue>;

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body?: unknown;

  constructor({ message, status, statusText, body }: { message?: string; status: number; statusText: string; body?: unknown }) {
    super(message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

const buildQueryString = (params?: QueryParams): string => {
  if (!params) {
    return '';
  }

  const search = new URLSearchParams();

  for (const [key, rawValue] of Object.entries(params)) {
    if (rawValue === undefined || rawValue === null) {
      continue;
    }

    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const value of values) {
      search.append(key, String(value));
    }
  }

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
};

const defaultParser = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  if (contentType.includes('text/')) {
    return response.text();
  }

  if (contentType.includes('form-urlencoded')) {
    const text = await response.text();
    return Object.fromEntries(new URLSearchParams(text));
  }

  return response.arrayBuffer();
};

const ensureHeaders = (headers?: HeadersInit): Headers => {
  const result = new Headers();

  if (headers instanceof Headers) {
    headers.forEach((value, key) => result.set(key, value));
    return result;
  }

  if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => result.set(key, value));
    return result;
  }

  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      if (value !== undefined) {
        result.set(key, String(value));
      }
    });
  }

  return result;
};

export type UseApiQueryOptions<TData, TError = ApiError> = {
  url: string;
  params?: QueryParams;
  method?: Exclude<RequestInit['method'], undefined>;
  headers?: HeadersInit;
  body?: BodyInit | Record<string, unknown>;
  fetchOptions?: Omit<RequestInit, 'method' | 'headers' | 'body'>;
  queryKey?: QueryKey;
  responseParser?: (response: Response) => Promise<TData>;
  transformResponse?: (raw: unknown, response: Response) => TData;
  queryOptions?: Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;
};

export type FetchContext<TData> = {
  url: string;
  params?: QueryParams;
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | Record<string, unknown>;
  fetchOptions?: Omit<RequestInit, 'method' | 'headers' | 'body'>;
  responseParser?: (response: Response) => Promise<TData>;
  transformResponse?: (raw: unknown, response: Response) => TData;
};

export const executeRequest = async <TData>({
  url,
  params,
  method = 'GET',
  headers,
  body,
  fetchOptions,
  responseParser,
  transformResponse,
}: FetchContext<TData>): Promise<TData> => {
  const queryString = buildQueryString(params);
  const targetUrl = `${BACKEND_URL}${API_VERSION}${url}${queryString}`;

  const normalizedHeaders = ensureHeaders(headers);

  // Add authorization token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    normalizedHeaders.set('Authorization', `Bearer ${token}`);
  }

  let requestBody: BodyInit | undefined;

  if (body instanceof Blob || body instanceof FormData || body instanceof URLSearchParams || typeof body === 'string') {
    requestBody = body;
  } else if (body && typeof body === 'object') {
    normalizedHeaders.set('Content-Type', normalizedHeaders.get('Content-Type') ?? 'application/json');
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(targetUrl, {
    method,
    headers: normalizedHeaders,
    body: method.toUpperCase() === 'GET' || method.toUpperCase() === 'HEAD' ? undefined : requestBody,
    ...fetchOptions,
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await defaultParser(response);
    } catch {
      errorBody = undefined;
    }

    if (response.status === 401 && globalLogoutCallback) {
      globalLogoutCallback();
    }

    throw new ApiError({
      status: response.status,
      statusText: response.statusText,
      body: errorBody,
      message: errorBody && typeof errorBody === 'object' && 'message' in errorBody ? String((errorBody as Record<string, unknown>).message) : undefined,
    });
  }

  const parsed = responseParser ? await responseParser(response) : await defaultParser(response);
  const data = transformResponse ? transformResponse(parsed, response) : (parsed as TData);

  return data;
};

export const useApiQuery = <TData = unknown, TError = ApiError>({
  url,
  params,
  method,
  headers,
  body,
  fetchOptions,
  queryKey,
  responseParser,
  transformResponse,
  queryOptions,
}: UseApiQueryOptions<TData, TError>): UseQueryResult<TData, TError> => {
  const queryKeyBody =
    body && typeof body === 'object' && !(body instanceof Blob) && !(body instanceof FormData) && !(body instanceof URLSearchParams)
      ? JSON.stringify(body)
      : body;
  const finalQueryKey: QueryKey = queryKey ?? ['api', method ?? 'GET', url, params, queryKeyBody];

  return useQuery<TData, TError>({
    queryKey: finalQueryKey,
    queryFn: () =>
      executeRequest<TData>({
        url,
        params,
        method,
        headers,
        body,
        fetchOptions,
        responseParser,
        transformResponse,
      }),
    ...(queryOptions ?? {}),
  });
};
